// Broadcast Detail Drawer (WP-02 / WhatChimp §1.5).
// Shows live campaign counters, timeline, recipient rows with status filter,
// cursor pagination, and Export CSV.

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../lib/i18n';
import {
  whatsappBroadcastDetail,
  whatsappBroadcastRecipients,
  whatsappExportBroadcastReport,
  type BroadcastDoc,
  type BroadcastCounters,
  type RecipientRow,
  type BcastStatus,
  formatScheduledTime,
} from '../../lib/broadcasts';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useToast } from '../ui/Toast';
import {
  DownloadIcon,
  ClockIcon,
  RefreshIcon,
} from '../icons';

interface BroadcastDetailDrawerProps {
  open: boolean;
  broadcastId: string | null;
  onClose: () => void;
}

const STATUS_BADGE: Record<
  BcastStatus,
  { variant: 'slate' | 'warning' | 'info' | 'success' | 'danger'; dot: boolean }
> = {
  draft: { variant: 'slate', dot: true },
  scheduled: { variant: 'warning', dot: true },
  sending: { variant: 'info', dot: true },
  completed: { variant: 'success', dot: true },
  paused: { variant: 'warning', dot: true },
  cancelled: { variant: 'danger', dot: true },
  failed: { variant: 'danger', dot: true },
};

const RECIPIENT_STATUS_BADGE: Record<
  string,
  'slate' | 'warning' | 'info' | 'success' | 'danger'
> = {
  queued: 'slate',
  sent: 'info',
  sandbox: 'info',
  delivered: 'success',
  read: 'success',
  failed: 'danger',
  unreached: 'danger',
};

export function BroadcastDetailDrawer({
  open,
  broadcastId,
  onClose,
}: BroadcastDetailDrawerProps) {
  const { t } = useI18n();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [broadcast, setBroadcast] = useState<BroadcastDoc | null>(null);
  const [counters, setCounters] = useState<BroadcastCounters | null>(null);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Status Filter for recipients
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Initial load
  const loadDetail = useCallback(async () => {
    if (!broadcastId) return;
    setLoading(true);
    try {
      const res = await whatsappBroadcastDetail({ id: broadcastId, limit: 50 });
      if (res.ok) {
        setBroadcast(res.broadcast);
        setCounters(res.counters);
        setRecipients(res.recipients || []);
        setNextCursor(res.nextCursor);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
    // `toast` deliberately excluded: an error toast must not re-run the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId]);

  useEffect(() => {
    if (open && broadcastId) {
      setStatusFilter('ALL');
      void loadDetail();
    } else {
      setBroadcast(null);
      setCounters(null);
      setRecipients([]);
      setNextCursor(null);
    }
  }, [open, broadcastId, loadDetail]);

  // Filter recipients
  async function handleFilterChange(newStatus: string) {
    if (!broadcastId) return;
    setStatusFilter(newStatus);
    setLoadingRecipients(true);
    try {
      const res = await whatsappBroadcastRecipients({
        id: broadcastId,
        status: newStatus === 'ALL' ? undefined : newStatus,
        limit: 50,
      });
      if (res.ok) {
        setRecipients(res.recipients || []);
        setNextCursor(res.nextCursor);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoadingRecipients(false);
    }
  }

  // Load more recipients
  async function handleLoadMore() {
    if (!broadcastId || !nextCursor) return;
    setLoadingRecipients(true);
    try {
      const res = await whatsappBroadcastRecipients({
        id: broadcastId,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        cursor: nextCursor,
        limit: 50,
      });
      if (res.ok) {
        setRecipients((prev) => [...prev, ...(res.recipients || [])]);
        setNextCursor(res.nextCursor);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoadingRecipients(false);
    }
  }

  // Export CSV
  async function handleExport() {
    if (!broadcastId) return;
    setExporting(true);
    try {
      const res = await whatsappExportBroadcastReport({ id: broadcastId });
      if (res.ok && res.downloadUrl) {
        window.open(res.downloadUrl, '_blank');
        toast.success(t('bcast.exportSuccess') || `Exported ${res.totalRows} recipient rows`);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  }

  const reach = broadcast?.estimatedReach || counters?.queued || 1;
  const processed = counters?.processed || 0;
  const delivered = counters?.delivered || 0;
  const opened = counters?.read || counters?.opened || 0;

  const processedPct = Math.min(100, Math.round((processed / reach) * 100));
  const deliveredPct = reach > 0 ? Math.min(100, Math.round((delivered / reach) * 100)) : 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={broadcast?.name || t('bcast.detail.title') || 'Campaign Details'}
      subtitle={
        broadcast?.templateName
          ? `${t('bcast.detail.template') || 'Template'}: ${broadcast.templateName} (${broadcast.templateLanguage?.toUpperCase() || 'EN'})`
          : undefined
      }
    >
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : !broadcast ? (
        <div className="py-12 text-center text-xs text-ink-3">
          {t('bcast.detail.notFound') || 'Campaign not found.'}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Top Bar: Status, Actions & Export */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface/50 p-4">
            <div className="flex items-center gap-3">
              <Badge
                variant={STATUS_BADGE[broadcast.status]?.variant || 'slate'}
                dot={STATUS_BADGE[broadcast.status]?.dot}
              >
                {t(`status.${broadcast.status}`)}
              </Badge>

              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-ink-2">
                {t(`bcast.type.${broadcast.type}.tag`) || broadcast.type}
              </span>

              {broadcast.scheduledFor && (
                <span className="flex items-center gap-1 text-xs text-ink-3">
                  <ClockIcon size={14} />
                  {formatScheduledTime(broadcast.scheduledFor, broadcast.timezone)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                <DownloadIcon size={14} className="me-1.5" />
                {exporting ? t('common.exporting') || 'Exporting…' : t('bcast.actions.exportCsv') || 'Export CSV'}
              </Button>

              <Button variant="ghost" size="sm" onClick={() => void loadDetail()}>
                <RefreshIcon size={14} />
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="rounded-xl border border-line bg-white p-4 space-y-2 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-ink">
              <span>{t('bcast.detail.deliveryProgress') || 'Delivery Progress'}</span>
              <span>
                {processed} / {reach} ({processedPct}%)
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full bg-brand transition-all duration-500 rounded-full"
                style={{ width: `${processedPct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-ink-3">
              <span>{delivered} {t('bcast.detail.deliveredLabel') || 'delivered'} ({deliveredPct}%)</span>
              <span>{opened} {t('bcast.detail.openedLabel') || 'opened'}</span>
            </div>
          </div>

          {/* 6 Counter Chips */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-ink-3 block">Queued</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.queued?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-sky-600 block">Sent</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.sent?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-emerald-600 block">Delivered</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.delivered?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-amber-600 block">Opened</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.read?.toLocaleString() || counters?.opened?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-rose-600 block">Failed</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.failed?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="rounded-lg border border-line bg-surface/50 p-3">
              <span className="text-[11px] font-semibold text-slate-500 block">Unreached</span>
              <span className="text-lg font-bold text-ink mt-0.5 block">
                {counters?.unreached?.toLocaleString() || '0'}
              </span>
            </div>
          </div>

          {/* Execution Timeline */}
          <div className="rounded-xl border border-line bg-white p-4 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-ink-3 mb-3">
              {t('bcast.detail.timeline') || 'Execution Timeline'}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-ink-3 block">Created</span>
                <span className="font-semibold text-ink">
                  {new Date(broadcast.createdAt).toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              {broadcast.scheduledFor && (
                <div>
                  <span className="text-ink-3 block">Scheduled For</span>
                  <span className="font-semibold text-amber-600">
                    {formatScheduledTime(broadcast.scheduledFor, broadcast.timezone)}
                  </span>
                </div>
              )}
              {broadcast.pausedAt && (
                <div>
                  <span className="text-ink-3 block">Paused At</span>
                  <span className="font-semibold text-amber-600">
                    {new Date(broadcast.pausedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
              {broadcast.completedAt && (
                <div>
                  <span className="text-ink-3 block">Completed At</span>
                  <span className="font-semibold text-emerald-600">
                    {new Date(broadcast.completedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Recipients Section */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-ink-3">
                {t('bcast.detail.recipients') || 'Recipients'} ({recipients.length})
              </h4>

              {/* Status Filter tabs */}
              <div className="flex flex-wrap gap-1">
                {['ALL', 'delivered', 'read', 'failed', 'unreached', 'sent', 'queued'].map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => handleFilterChange(st)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      statusFilter === st
                        ? 'bg-brand text-white'
                        : 'bg-slate-100 text-ink-2 hover:bg-slate-200'
                    }`}
                  >
                    {st === 'ALL' ? 'All' : st}
                  </button>
                ))}
              </div>
            </div>

            {/* Recipients Table */}
            <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-start text-xs">
                  <thead className="bg-surface border-b border-line text-ink-3 font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="px-3 py-2.5 text-start">Phone</th>
                      <th className="px-3 py-2.5 text-start">Name</th>
                      <th className="px-3 py-2.5 text-start">Status</th>
                      <th className="px-3 py-2.5 text-start">Delivered</th>
                      <th className="px-3 py-2.5 text-start">Read</th>
                      <th className="px-3 py-2.5 text-start">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {loadingRecipients ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-ink-3">
                          Loading recipients…
                        </td>
                      </tr>
                    ) : recipients.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-ink-3">
                          No recipients match the filter.
                        </td>
                      </tr>
                    ) : (
                      recipients.map((r) => (
                        <tr key={r.id || r.subscriberId} className="hover:bg-surface/50">
                          <td className="px-3 py-2 font-mono font-medium text-ink">
                            {r.phoneE164}
                          </td>
                          <td className="px-3 py-2 text-ink-2">{r.name || '—'}</td>
                          <td className="px-3 py-2">
                            <Badge
                              variant={RECIPIENT_STATUS_BADGE[r.status] || 'slate'}
                              dot
                            >
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-ink-3">
                            {r.deliveredAt
                              ? new Date(r.deliveredAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-ink-3">
                            {r.readAt
                              ? new Date(r.readAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-rose-600 font-mono text-[11px]">
                            {r.error || (r.errorCode ? `Code ${r.errorCode}` : '—')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {nextCursor && (
                <div className="p-2 border-t border-line text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={loadingRecipients}
                  >
                    {loadingRecipients ? 'Loading…' : 'Load More Recipients ↓'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
